/* ==========================================================================
   FILE: global/context/ThemeContext.jsx
   IAMONEAI - Theme Context Provider
   Manages dark/light mode state across the application
   ========================================================================== */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import {
  THEMES,
  DEFAULT_THEME,
  getStoredTheme,
  setStoredTheme,
  resolveTheme,
  applyTheme,
  onSystemThemeChange,
  initializeTheme,
} from '../config/theme';

// ==========================================================================
// CONTEXT CREATION
// ==========================================================================

const ThemeContext = createContext(null);

// ==========================================================================
// PROVIDER COMPONENT
// ==========================================================================

export function ThemeProvider({ children }) {
  // Initialize with stored theme or default
  const [theme, setThemeState] = useState(() => {
    return getStoredTheme() || DEFAULT_THEME;
  });

  // Resolved theme (actual 'light' or 'dark', never 'system')
  const [resolvedTheme, setResolvedTheme] = useState(() => {
    return resolveTheme(theme);
  });

  // ==========================================================================
  // THEME APPLICATION
  // ==========================================================================

  /**
   * Set theme and persist to storage
   */
  const setTheme = useCallback((newTheme) => {
    if (!Object.values(THEMES).includes(newTheme)) {
      console.warn(`Invalid theme: ${newTheme}`);
      return;
    }

    setThemeState(newTheme);
    setStoredTheme(newTheme);

    const resolved = resolveTheme(newTheme);
    setResolvedTheme(resolved);
    applyTheme(resolved);
  }, []);

  /**
   * Toggle between light and dark (skips system)
   */
  const toggleTheme = useCallback(() => {
    const newTheme = resolvedTheme === THEMES.DARK ? THEMES.LIGHT : THEMES.DARK;
    setTheme(newTheme);
  }, [resolvedTheme, setTheme]);

  /**
   * Cycle through all themes: dark -> light -> system -> dark
   */
  const cycleTheme = useCallback(() => {
    const order = [THEMES.DARK, THEMES.LIGHT, THEMES.SYSTEM];
    const currentIndex = order.indexOf(theme);
    const nextIndex = (currentIndex + 1) % order.length;
    setTheme(order[nextIndex]);
  }, [theme, setTheme]);

  // ==========================================================================
  // INITIALIZATION & SYSTEM THEME LISTENER
  // ==========================================================================

  // Apply theme on mount
  useEffect(() => {
    const initialTheme = initializeTheme();
    setThemeState(getStoredTheme() || initialTheme);
    setResolvedTheme(resolveTheme(initialTheme));
  }, []);

  // Listen for system theme changes (only relevant when theme is 'system')
  useEffect(() => {
    if (theme !== THEMES.SYSTEM) return;

    const cleanup = onSystemThemeChange((systemTheme) => {
      setResolvedTheme(systemTheme);
      applyTheme(systemTheme);
    });

    return cleanup;
  }, [theme]);

  // ==========================================================================
  // CONTEXT VALUE
  // ==========================================================================

  const value = {
    // Current theme setting ('light', 'dark', or 'system')
    theme,
    
    // Resolved theme (always 'light' or 'dark')
    resolvedTheme,
    
    // Theme setters
    setTheme,
    toggleTheme,
    cycleTheme,
    
    // Convenience booleans
    isDark: resolvedTheme === THEMES.DARK,
    isLight: resolvedTheme === THEMES.LIGHT,
    isSystem: theme === THEMES.SYSTEM,
    
    // Theme constants
    themes: THEMES,
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
}

// ==========================================================================
// HOOK
// ==========================================================================

/**
 * Hook to access theme context
 * @returns {Object} Theme context value
 * @throws {Error} If used outside ThemeProvider
 */
export function useTheme() {
  const context = useContext(ThemeContext);
  
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  
  return context;
}

// ==========================================================================
// EXPORTS
// ==========================================================================

export { ThemeContext };
export default ThemeProvider;